/**
 * Ordering rules for the shared proposal sections ("Why Decks Unique" blocks),
 * used by the in-place editor on the Proposal page. Pure — no I/O — so the
 * rules are unit-testable.
 *
 * Two stores must stay in step, exactly as the Settings editor keeps them:
 *   - org_proposal_sections rows, saved in list order (that order becomes
 *     sort_order via upsertProposalSections);
 *   - org_settings.proposal_layout_order, the list the proposal renders from.
 *     It also carries two system ids, "__details__" and "__timeline__", which
 *     are preserved where they are.
 */
import type { ProposalSection } from "../proposalSections";

export const SYSTEM_LAYOUT_IDS = ["__details__", "__timeline__"] as const;

const isSystem = (id: string) =>
  (SYSTEM_LAYOUT_IDS as readonly string[]).includes(id);

/** Same merge the Settings editor uses: keep saved ids that still exist, then
 *  append missing system ids, then any section missing from the saved order. */
export function mergeLayoutOrder(
  layoutOrder: string[] | null | undefined,
  sections: ProposalSection[]
): string[] {
  const ids = new Set(sections.map((s) => String(s.id).trim()));
  const saved = (layoutOrder || []).map((x) => String(x).trim()).filter(Boolean);
  const valid = saved.filter((id) => isSystem(id) || ids.has(id));
  const missingSystem = SYSTEM_LAYOUT_IDS.filter((id) => !valid.includes(id));
  const missingCustom = sections
    .map((s) => String(s.id).trim())
    .filter((id) => !valid.includes(id));
  const out = [...valid, ...missingSystem, ...missingCustom];
  return out.filter((v, i) => out.indexOf(v) === i);
}

/** Sections in the order they print/save (system ids dropped). */
export function orderedSections(
  order: string[],
  sections: ProposalSection[]
): ProposalSection[] {
  const byId = new Map(sections.map((s) => [String(s.id).trim(), s]));
  return order
    .filter((id) => !isSystem(id))
    .map((id) => byId.get(id))
    .filter(Boolean) as ProposalSection[];
}

/** Move a section one step among the sections on screen. By default that is
 *  the ENABLED ones (what prints); an admin's editor also shows hidden
 *  sections, so it passes `isVisible = () => true`. Sections that are not on
 *  screen and system ids keep their slots, so an arrow press always visibly
 *  moves the block past its neighbour. Returns the order unchanged when there
 *  is nowhere to go. */
export function moveSectionVisible(
  order: string[],
  sections: ProposalSection[],
  id: string,
  dir: -1 | 1,
  isVisible: (s: ProposalSection) => boolean = (s) => !!s.enabled
): string[] {
  const visibleIds = new Set(
    sections.filter(isVisible).map((s) => String(s.id).trim())
  );
  const visible = order.filter((x) => visibleIds.has(x));
  const vi = visible.indexOf(id);
  const neighbour = visible[vi + dir];
  if (vi < 0 || neighbour === undefined) return order;
  const a = order.indexOf(id);
  const b = order.indexOf(neighbour);
  const next = [...order];
  [next[a], next[b]] = [next[b], next[a]];
  return next;
}

/** Order with a new section appended after the last on-screen section (so
 *  it appears at the end of Why Decks Unique, before any system ids that
 *  follow). Visibility as in moveSectionVisible. */
export function insertSectionAfterVisible(
  order: string[],
  sections: ProposalSection[],
  newId: string,
  isVisible: (s: ProposalSection) => boolean = (s) => !!s.enabled
): string[] {
  const visibleIds = new Set(
    sections
      .filter((s) => isVisible(s) || String(s.id).trim() === newId)
      .map((s) => String(s.id).trim())
  );
  let lastVisible = -1;
  order.forEach((x, i) => {
    if (x !== newId && visibleIds.has(x)) lastVisible = i;
  });
  const next = order.filter((x) => x !== newId);
  next.splice(lastVisible + 1, 0, newId);
  return next;
}
